import { TradeIdea, TradeIdeaResponse, TradeIdeaRequestParams } from './entities';
import { DateTime } from 'luxon';
import { Subscribable } from '@benzinga/subscribable';
import { TradeIdeaRequest } from './request';
import { SafePromise } from '@benzinga/safe-await';

interface NewTradeIdeasEvent {
  count: number;
  type: 'new_trade_idea';
}

interface AddedTradeIdeasEvent {
  type: 'added_trade_idea';
  idea: TradeIdea;
}

export type TradeIdeaEvent = NewTradeIdeasEvent | AddedTradeIdeasEvent;

export class TradeIdeaFeed extends Subscribable<TradeIdeaEvent> {
  request: TradeIdeaRequest;
  params: TradeIdeaRequestParams = {};
  tradeIdeas: Array<TradeIdea> = [];
  isLoading = false;
  hasMore = true;
  limit = 7;
  numNewIdeas = 0;
  isWatching = false;
  watcher: any;

  constructor(params: TradeIdeaRequestParams, request: TradeIdeaRequest) {
    super();
    this.params = params;
    if (params.limit) {
      this.limit = params.limit;
    }
    this.request = request;
  }

  public setParams = (params: TradeIdeaRequestParams): void => {
    this.params = params;
    if (params.limit) {
      this.limit = params.limit;
    }
  };

  public watch = (): void => {
    this.isWatching = true;
    if (!this.watcher) {
      this.watcher = setInterval(() => {
        this.hasNewTradeIdeas();
      }, 60000);
    }
  };

  public stop = (): void => {
    if (this.watcher) {
      clearInterval(this.watcher);
      this.isWatching = false;
    }
  };

  public loadMoreTradeIdeas = async (): SafePromise<TradeIdea[]> => {
    this.isLoading = true;
    const tradeIdeaResponse = await this.find({
      limit: this.limit,
      next_id: this.params.next_id,
    });
    this.isLoading = false;
    if (tradeIdeaResponse.err) {
      return tradeIdeaResponse as unknown as SafePromise<TradeIdea[]>;
    } else {
      if (tradeIdeaResponse?.ok?.data.length < this.limit) this.hasMore = false;
      return { ok: (this.tradeIdeas = this.tradeIdeas.concat(tradeIdeaResponse.ok.data)) };
    }
  };

  public loadTradeIdeas = async (): SafePromise<TradeIdea[]> => {
    const t = Math.floor(DateTime.now().toSeconds() / 60);
    this.isLoading = true;
    const tradeIdeaResponse = await this.find({
      limit: this.limit,
      t: t,
    });
    this.isLoading = false;
    if (tradeIdeaResponse.err) {
      return tradeIdeaResponse as unknown as SafePromise<TradeIdea[]>;
    } else {
      if (tradeIdeaResponse?.ok?.data.length < this.limit) this.hasMore = false;
      return { ok: (this.tradeIdeas = this.tradeIdeas.concat(tradeIdeaResponse.ok.data)) };
    }
  };

  public hasNewTradeIdeas = async (): SafePromise<void | number> => {
    const t = Math.floor(DateTime.now().toSeconds() / 60);
    const first = this.tradeIdeas[0];
    if (!first) return { ok: undefined };
    const tradeIdeaResponse = await this.find({
      last_id: first?.id,
      t: t,
    });
    if (tradeIdeaResponse.err) {
      return tradeIdeaResponse as unknown as SafePromise<void | number>;
    } else {
      this.numNewIdeas = tradeIdeaResponse.ok.data.length;
      if (this.numNewIdeas) {
        this.dispatch({
          count: this.numNewIdeas,
          type: 'new_trade_idea',
        });
      }
      return { ok: this.numNewIdeas };
    }
  };

  public addTradeIdea = (idea: TradeIdea): void => {
    this.tradeIdeas.unshift(idea);
    this.dispatch({
      idea: idea,
      type: 'added_trade_idea',
    });
  };

  public reloadTradeIdeas = async (): SafePromise<TradeIdea[]> => {
    this.reset();
    return this.loadTradeIdeas();
  };

  public reset = () => {
    this.tradeIdeas = [];
    this.numNewIdeas = 0;
  };

  private find = (additionalParams: {
    last_id?: number;
    next_id?: number;
    t?: number;
    limit?: number;
  }): SafePromise<TradeIdeaResponse> => {
    const params: TradeIdeaRequestParams = {
      filter: this.params.filter,
      node: this.params.node,
      tag: this.params.tag,
      tickers: this.params.tickers,
      trader: this.params.trader,
      verified: this.params.verified,
      ...(additionalParams ?? {}),
    };
    const response = this.request.find(params);
    return response;
  };
}
